import {
  Box,
  Divider,
  Grid,
  HStack,
  Modal,
  ModalContent,
  ModalOverlay,
  Text
} from '@chakra-ui/react';
import React, { FC } from 'react';
import { useToggle } from 'react-use';
import { AddLiquiditySectionTitle } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquiditySectionTitle';

import { IDerivedMintInfoWithProvider } from '@app/state/mintStore';
import { SubmitButton } from '@app/components/SubmitButton';
import { TokensPair } from '@app/components/TokensPair';
import { formatNumber, formatWalletAddress } from '@app/helpers/format';
import { CopyButton } from '@app/components/CopyButton';
import { AddLiquidityWithProviderButtonETH } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquidityWithProviderButtonETH';
import AddLiquidityWithProviderButton from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquidityWithProviderButton';
import { Currency } from '@cryptoalgebra/kim-sdk';
import { Web3Button } from '@thirdweb-dev/react';
import clsx from 'clsx';
import s from '@app/screens/airdrop/ClaimsList/ClaimsList.module.scss';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { LockSection } from '@app/screens/liquidity/AddLiquidityV3/components/LockSection';
import { InfoRow } from '@app/screens/liquidity/AddLiquidityV3/components/InfoRow';
import { useFarmBaseApr } from '@app/hooks/plugins/useFarmBaseApr';
import { useConfig } from '@app/config';
import { TokensTitle } from '@app/screens/liquidity/AddLiquidityV3/components/TokensTitle';

interface Props {
  currencyA: Currency | undefined;
  currencyB: Currency | undefined;
  mintInfo: IDerivedMintInfoWithProvider;
}

export const CreatePositionWithKpNft: FC<Props> = ({
  mintInfo,
  currencyA,
  currencyB
}) => {
  const config = useConfig();
  const [open, toggleOpen] = useToggle(false);

  const { farmBaseApr, lockBonusApr, totalApr } = useFarmBaseApr(
    mintInfo.nftPoolAddress,
    mintInfo.selectedVault?.tvl,
    mintInfo.selectedVault?.apr,
    mintInfo.lockDuration
  );

  if (!mintInfo.token0 || !mintInfo.token1) {
    return null;
  }

  const isReady =
    mintInfo.parsedAmounts.CURRENCY_A?.greaterThan(0) &&
    mintInfo.parsedAmounts.CURRENCY_B?.greaterThan(0);

  return (
    <>
      <SubmitButton
        isLoading={false}
        label={mintInfo.errorMessage ?? 'Create position'}
        onClick={toggleOpen}
        disabled={!isReady || !!mintInfo.errorMessage}
      />
      <Modal
        isOpen={open}
        onClose={() => {
          toggleOpen(false);
        }}
        isCentered
        motionPreset="slideInBottom"
        size="xs"
      >
        <ModalOverlay />
        <ModalContent
          sx={{ maxWidth: ['100vw', '586px'], padding: ['0', '24px'] }}
        >
          <AddLiquiditySectionTitle
            isCompleted={false}
            label="Create position"
            hideStatus
          />
          <Divider
            sx={{
              width: '100%',
              background: 'neutral.500',
              height: '1px',
              my: 3
            }}
          />
          <TokensTitle
            token0Symbol={mintInfo.token0.symbol ?? ''}
            token1Symbol={mintInfo.token1.symbol ?? ''}
            address={mintInfo.vaultAddress ?? ''}
          />

          <LockSection mintInfo={mintInfo} />

          <AddLiquiditySectionTitle
            isCompleted={false}
            label="Estimates"
            hideStatus
          />

          <Box p="0 14px">
            <InfoRow
              label="Total APR"
              value={`${totalApr ? formatNumber(totalApr, 2) : 0}%`}
            />
            <InfoRow
              label="Fee APR"
              value={`${mintInfo.selectedVault?.apr ?? 0}%`}
            />
            <InfoRow
              label="Farm Base APR"
              value={`${formatNumber(farmBaseApr, 2)}%`}
            />
            <InfoRow
              label="Lock Bonus APR"
              value={`${lockBonusApr ? formatNumber(lockBonusApr, 2) : 0}%`}
            />
          </Box>

          {mintInfo.nftPoolAddress === null ? (
            <>
              <Alert
                status="warning"
                display="flex"
                border="1px solid"
                borderColor="neutral.700"
                my="16px"
                borderRadius="10px"
                py="10px"
                px="12px"
              >
                <AlertIcon w="16px" h="16px" mr="8px" color="neutral.400" />
                <AlertDescription color="neutral.400">
                  You are the very first kpNFT minter for this asset! You will
                  need to initialize kpNFT contract first.
                </AlertDescription>
              </Alert>

              <Web3Button
                className={clsx(s.claimBtn, s.outlined)}
                contractAddress={config?.CONTRACTS.NFT_POOL_FACTORY!}
                action={async contract =>
                  contract.call('createPool', [mintInfo.vaultAddress])
                }
                onError={error => {
                  console.error(error);
                  showErrorToast('Failed to initialize pool');
                }}
                onSuccess={async () => {
                  showSuccessToast('Successfully initialized NFT pool');
                  await mintInfo.getPoolAddress();
                }}
                isDisabled={false}
              >
                Initialize NFT pool
              </Web3Button>
            </>
          ) : (
            <>
              {currencyA?.isNative || currencyB?.isNative ? (
                <AddLiquidityWithProviderButtonETH
                  baseCurrency={currencyA}
                  quoteCurrency={currencyB}
                  mintInfo={mintInfo}
                  poolAddress={mintInfo.vaultAddress}
                  onComplete={toggleOpen}
                />
              ) : (
                <AddLiquidityWithProviderButton
                  baseCurrency={currencyA}
                  quoteCurrency={currencyB}
                  mintInfo={mintInfo}
                  poolAddress={mintInfo.vaultAddress}
                  onComplete={toggleOpen}
                />
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
